import React, { useRef, useEffect, useState, useCallback } from 'react';
import { View, Button, Heading, colors, Icon } from '@fairygodboss/ui';
import Slider from 'react-slick';
import uuid from 'uuid/v4';
import './slider.scss';
import { GridColumnWrapper } from '../../../Events/EventLandingPage/components/GridColumnWrapper';
import { useAdvicePage } from '../AdvicePageContext';
import { CommunityDiscussionCard } from './CommunityDiscussionCard';
import { EditorialContentCard } from './EditorialContentCard';
import { labelToTextMap } from '../adviceHashMap';
import { FeauturedArticle } from './FeautredArticle';
import { FeaturedCommunityDiscussionCard } from './FeaturedCommunityDiscussionCard';
import './exploreTopics.scss';

export const ShowMoreMobileButton = ({
    onPress,
    content,
    mobileSlidesToShow,
}) => {
    const isLastPage = mobileSlidesToShow >= content.length;

    return (
        <Button
            size="large"
            variant="primaryText"
            px="20px"
            py="10px"
            rightIcon={
                <Icon
                    outlineColor={colors.primary[60]}
                    variant={isLastPage ? 'chevron_up_16' : 'chevron_down_16'}
                />
            }
            onPress={onPress}
        >
            Show {isLastPage ? 'less' : 'more'}
        </Button>
    );
};

const TopicButton = ({ term, onResetCollapse, ...others }) => {
    let _term = term;
    if (term === 'View all topics') {
        _term = null;
    }

    const { onTopicSelect, currentTopic } = useAdvicePage();

    const active =
        term === currentTopic || (term === 'View all topics' && !currentTopic);

    return (
        <Button
            onPress={() => {
                onResetCollapse(term);
                onTopicSelect({ topic: _term });
            }}
            mr={{ _: '8px', sm: '16px' }}
            mt="16px"
            variant="secondaryText"
            size="large"
            px={{ _: '16px', sm: '20px' }}
            py={{ _: '7px', sm: '11px' }}
            css={{
                backgroundColor: colors.secondary[active ? 60 : 10],
                '@media (hover: hover) and (pointer: fine)': {
                    ':hover': {
                        backgroundColor: active
                            ? colors.secondary[60]
                            : colors.secondary[20],
                    },
                },
                span: {
                    color: colors.neutral[active ? 0 : 100],
                    letterSpacing: '-0.4px',
                    fontSize: '14px',
                    lineHeight: '18px',
                },
            }}
            {...others}
        >
            {labelToTextMap[term] || term}
        </Button>
    );
};

const ItemContainer = ({ content, handleOnLoad, index }) => {
    const { cardDimensions } = useAdvicePage();
    const type = content?.type;
    if (!type) {
        return null;
    }

    return (
        <View width={`${cardDimensions?.width}px`} height="100%">
            {type === 'community-discussion' ? (
                <CommunityDiscussionCard
                    index={index}
                    content={content}
                    carouselView={cardDimensions}
                />
            ) : (
                <EditorialContentCard
                    index={index}
                    handleOnLoad={handleOnLoad}
                    content={content}
                    carouselView={cardDimensions}
                />
            )}
        </View>
    );
};

export const ExploreTopics = () => {
    const sliderRef = useRef();

    const {
        content,
        isSm,
        currentTopic,
        currentSlide: _currentSlide,
        updateCurrentSlide,
        isXs,
        windowWidth,
    } = useAdvicePage();

    useEffect(() => {
        if (typeof _currentSlide !== 'number') {
            updateCurrentSlide(0);
            if (sliderRef.current) sliderRef.current.slickGoTo(0);
        }
    }, [_currentSlide, updateCurrentSlide]);

    const [slidesToLoad, setSlidesToLoad] = useState(6 * _currentSlide);
    const [mobileSlidesToShow, setMobileSlidesToShow] = useState(
        _currentSlide * 6
    );

    const scrollToTopOfCarousel = useCallback(() => {
        const _el = document.getElementById('advice-carousel');
        if (_el) {
            _el.scrollIntoView({
                block: 'start',
                behavior: 'smooth',
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [content]);

    const currentSlideRef = useRef(_currentSlide - 1);
    const isSmRef = useRef(isSm);

    useEffect(() => {
        isSmRef.current = isSm;
    }, [isSm]);

    useEffect(() => {
        let didFinishLoading = false;
        const slidesToShow = isSm ? 2 : 3;
        const initialSide = _currentSlide > 0 ? _currentSlide : 1;
        const slidetoUse = (Math.ceil(initialSide) - 1) * slidesToShow;
        if (sliderRef.current && slidetoUse !== initialSide) {
            if (didFinishLoading) clearTimeout(didFinishLoading);
            didFinishLoading = setTimeout(() => {
                sliderRef.current.slickGoTo(slidetoUse);
            }, 200);
        }
    }, [isSm, windowWidth, _currentSlide]);

    const updateSlideRef = (currentSlide, slide, topic = null) => {
        const slidesToShow = isSmRef.current ? 2 : 3;
        const slideOn = Math.ceil(currentSlide / slidesToShow) * slidesToShow;
        const pageNumber = slideOn / slidesToShow + slide;
        const slideToUse = slideOn + slide * slidesToShow;
        const goToSlide = slideToUse > 0 ? Math.ceil(slideToUse) : 0;
        sliderRef.current.slickGoTo(goToSlide);
        sliderRef.current.slidesToShow = slidesToShow;
        updateCurrentSlide(pageNumber + 1);
        window.history.replaceState(
            {},
            '',
            `/advice${topic ? `/${topic}` : ''}?page=${pageNumber + 1}`
        );
        const slideToSet = (pageNumber + 1) * 6;
        setMobileSlidesToShow(slideToSet);
        requestAnimationFrame(() => scrollToTopOfCarousel());
    };
    const PrevArrow = (props) => {
        const { className, currentSlide } = props;
        const { currentTopic: topic } = useAdvicePage();
        return (
            <Button
                disabled={className?.includes('slick-disabled')}
                leftIcon={
                    <Icon outlineColor="#1B2B4A" variant="chevron_left_16" />
                }
                onPress={() => {
                    updateSlideRef(currentSlide, -1, topic);
                }}
                size="medium"
                variant="secondaryText"
                position="absolute"
                left="calc(50% - 93px)"
                bottom="-38px"
                zIndex="1"
            >
                Prev
            </Button>
        );
    };

    useEffect(() => {
        setTimeout(() => {
            const el = document.getElementById('next-button');
            if (el) {
                el.setAttribute('ontouchstart', true);
            }
        }, 500);
    }, []);

    const NextArrow = (props) => {
        const { className, currentSlide } = props;
        const { currentTopic: topic } = useAdvicePage();
        return (
            <Button
                id="next-button"
                ml="12px"
                disabled={className?.includes('slick-disabled')}
                rightIcon={
                    <Icon outlineColor="#1B2B4A" variant="chevron_right_16" />
                }
                onPress={() => {
                    updateSlideRef(currentSlide, 1, topic);
                }}
                size="medium"
                variant="secondaryText"
                left="calc(50% + -4px)"
                bottom="-38px"
            >
                Next
            </Button>
        );
    };

    const beforeSlideChange = () => {
        setSlidesToLoad((prev) => {
            if (slidesToLoad < content.length) {
                return prev + 6;
            }
            return prev;
        });
    };

    const startingSlide = _currentSlide > 0 ? _currentSlide : 0;
    const initalCurrentSlide = (startingSlide - 1) * (isSmRef.current ? 2 : 3);

    const [settings, setSettings] = useState({
        dots: false,
        currentTopic,
        swipe: false,
        speed: 200,
        infinite: false,
        adaptiveHeight: true,
        slidesToShow: 3,
        slidesToScroll: 3,
        initialSlide: initalCurrentSlide,
        rows: 2,
        beforeChange: beforeSlideChange,
        prevArrow: <PrevArrow />,
        nextArrow: <NextArrow />,
        responsive: [
            {
                breakpoint: 905,
                settings: {
                    rows: 3,
                    slidesToShow: 2,
                    initialSlide: initalCurrentSlide,
                    slidesToScroll: 2,
                    dots: false,
                    swipe: false,
                    infinite: false,
                    adaptiveHeight: true,
                },
            },
        ],
    });

    const [rerenderKey, setRerenderKey] = useState(0);

    useEffect(() => {
        const randomNumber = Math.floor(Math.random() * 1001);
        if (currentTopic) {
            currentSlideRef.current = 0;
            setSettings((prev) => ({
                ...prev,
                initialSlide:
                    prev.currentTopic !== currentTopic ? 0 : prev.initialSlide,
            }));
            if (sliderRef.current) sliderRef.current.slickGoTo(0);
        }
        setRerenderKey(randomNumber + 1);
    }, [currentTopic, content, updateCurrentSlide, sliderRef]);

    const getCollapsedState = () => {
        const isCollapsed = mobileSlidesToShow < content.length;
        return isCollapsed;
    };

    const [collapsed, setCollapsed] = useState(getCollapsedState());

    useEffect(() => {
        setCollapsed(getCollapsedState());
    }, [isXs]); // Note: Having only one dependency here its intentional we only want this trigger on resize

    const onShowMorePress = () => {
        if (mobileSlidesToShow + 6 > content.filter((i) => !!i).length) {
            if (!collapsed) {
                setMobileSlidesToShow(6);
                window.history.replaceState(
                    {},
                    '',
                    `/advice${currentTopic ? `/${currentTopic}` : ''}?page=1`
                );
                currentSlideRef.current = 0;
                updateCurrentSlide(currentSlideRef.current);
                scrollToTopOfCarousel();
                setCollapsed(true);
            } else {
                const slideToSet = mobileSlidesToShow + 6;
                setMobileSlidesToShow(slideToSet);
                window.history.replaceState(
                    {},
                    '',
                    `/advice${currentTopic ? `/${currentTopic}` : ''}?page=${
                        slideToSet / 6
                    }`
                );
                currentSlideRef.current = slideToSet / 6;
                updateCurrentSlide(currentSlideRef.current);
                setCollapsed(false);
            }
        } else {
            const slideToSet = mobileSlidesToShow + 6;
            setMobileSlidesToShow(slideToSet);
            currentSlideRef.current = slideToSet / 6;
            updateCurrentSlide(currentSlideRef.current);
            window.history.replaceState(
                {},
                '',
                `/advice${currentTopic ? `/${currentTopic}` : ''}?page=${
                    slideToSet / 6
                }`
            );
        }
    };

    const [isClient, setIsClient] = useState(false);
    useEffect(() => {
        setIsClient(true);
    }, []);

    const renderLastPageFill = () => {
        const totalPages = Math.ceil(content.length / 6);
        const fills = [];
        if (totalPages * 6 > content.length) {
            for (let i = 0; i < totalPages * 6 - content.length; i += 1) {
                fills.push(<div key={`placeholder-${uuid()}`} />);
            }
        }
        return fills;
    };

    return isClient ? (
        <GridColumnWrapper
            color={colors.neutral[100]}
            overflow="hidden"
            bg={colors.neutral[0]}
            gridAlignment={{ _: 'center', md: 'start' }}
            gridColumnSpacing={{ _: 12, sm: 12, md: 12, lg: 12 }}
            pt={{ _: '40px', md: '56px' }}
            px={{ _: '16px', sm: '16px', md: '24px', lg: '0px' }}
            css={{
                '.slick-track': {
                    display: 'flex',
                    flexWrap: 'wrap',
                },
                '.slick-slide': {
                    padding: isSm ? '8px' : '12px',
                },
                '.slick-list': {
                    overflow: 'visible',
                    marginLeft: '-12px',
                    marginRight: '-12px',
                },
            }}
        >
            <Heading as="h3" variant="headingMd">
                Explore topics
            </Heading>
            <View mr={{ _: '-13px', sm: '0px' }}>
                <TopicButton
                    term="View all topics"
                    onResetCollapse={() => {
                        setMobileSlidesToShow(6);
                        setCollapsed(true);
                    }}
                />
                {Object.keys(labelToTextMap).map((i) => (
                    <TopicButton
                        display={{
                            _: 'initial',
                            sm: i === 'featured-content' ? 'none' : 'initial',
                        }}
                        key={`${i}-topic`}
                        term={i}
                        onResetCollapse={() => {
                            setMobileSlidesToShow(6);
                            setCollapsed(true);
                        }}
                    />
                ))}
                <View id="advice-carousel" position="relative" top="-56px" />
            </View>
            {!isXs ? (
                <>
                    <View
                        key={currentTopic}
                        display={{ _: 'none', sm: 'inherit' }}
                        position="initial"
                        className="roll-out"
                        css={{
                            button: {
                                '&:active': {
                                    backgroundColor: colors.secondary[20],
                                },
                                '@media(hover: hover) and (pointer: fine)': {
                                    '&:hover': {
                                        /* Targeting devices with mouse cursor and :hover */
                                        backgroundColor: colors.secondary[20],
                                    },
                                },
                            },
                        }}
                    >
                        <span key={rerenderKey}>
                            <Slider ref={sliderRef} {...settings}>
                                {content.map((c, index) => {
                                    if (index > slidesToLoad) {
                                        return (
                                            <div key={`${c?.hash}-invisible`} />
                                        );
                                    }

                                    return c ? (
                                        <ItemContainer
                                            index={index}
                                            content={c}
                                            key={`${content?.hash}`}
                                        />
                                    ) : (
                                        <div key={`placeholder-${uuid()}`} />
                                    );
                                })}
                                {renderLastPageFill()}
                            </Slider>
                        </span>
                    </View>
                    <View
                        justifyContent="center"
                        mb="72px"
                        mt="40px"
                        display={{ _: 'none', sm: 'flex' }}
                    />
                </>
            ) : (
                <>
                    {content.length > 6 ? (
                        <View
                            flexDirection="column"
                            display={{ _: 'flex', sm: 'none' }}
                        >
                            {content.slice(0, mobileSlidesToShow).map((c) => {
                                if (!c) {
                                    return null;
                                }
                                if (c.type === 'editorial-content') {
                                    return (
                                        <View
                                            key={`${c?.hash}-list`}
                                            display="flex"
                                            pt="24px"
                                            width="100%"
                                        >
                                            <FeauturedArticle
                                                key={`${c?.hash}-list`}
                                                content={c}
                                            />
                                        </View>
                                    );
                                }
                                return (
                                    <View
                                        key={`${c?.hash}-list`}
                                        display="flex"
                                        pt="24px"
                                        width="100%"
                                    >
                                        <FeaturedCommunityDiscussionCard
                                            maxTitleLine={3}
                                            maxBodyLine={4}
                                            content={c}
                                            fixedHeight="100%"
                                        />
                                    </View>
                                );
                            })}
                            <View
                                width="100%"
                                justifyContent="center"
                                pt="24px"
                                display="flex"
                                pb="40px"
                                css={{
                                    button: { backgroundColor: 'transparent' },
                                }}
                            >
                                <ShowMoreMobileButton
                                    onPress={onShowMorePress}
                                    content={content}
                                    mobileSlidesToShow={mobileSlidesToShow}
                                />
                            </View>
                        </View>
                    ) : (
                        <View
                            pb="40px"
                            flexDirection="column"
                            display={{ _: 'flex', sm: 'none' }}
                        >
                            {content.map((c) => {
                                if (!c) {
                                    return null;
                                }
                                if (c.type === 'editorial-content') {
                                    return (
                                        <View
                                            display="flex"
                                            pt="24px"
                                            width="100%"
                                            key={`${c?.hash}-list`}
                                        >
                                            <FeauturedArticle content={c} />
                                        </View>
                                    );
                                }
                                return (
                                    <View
                                        key={`${c?.hash}-list`}
                                        display="flex"
                                        pt="24px"
                                        width="100%"
                                    >
                                        <FeaturedCommunityDiscussionCard
                                            maxTitleLine={3}
                                            maxBodyLine={4}
                                            content={c}
                                            fixedHeight="100%"
                                        />
                                    </View>
                                );
                            })}
                        </View>
                    )}
                </>
            )}
        </GridColumnWrapper>
    ) : null;
};

ExploreTopics.displayName = 'ExploreTopics';
